import * as t from 'io-ts';
import type { TypeOf } from 'io-ts';
import FIELD_RENDER_TYPES from '../../../fieldRenderTypes';
import type {
    ChoiceMultipleExpandedField } from '../parts/getChoiceMultipleExpandedRenderType';
import getChoiceMultipleExpandedRenderType, {
    CHOICE_MULTIPLE_RENDER_TYPE,
    ChoiceMultipleExpandedFieldC,
} from '../parts/getChoiceMultipleExpandedRenderType';
import getEvaluationProps, { RawEvaluationFieldC } from '../parts/getEvaluationProps';
import isType from '../../../../io-ts/isType';
import assertAllElementsAreDefined from '../../../../typeAssertions/undefinedInArray';
import OneOf from '../../../../io-ts/OneOf';
import { FieldCommonPropsC } from '../../types/FieldCommonProps';
import { ChoiceValueC } from './toSelectFormat';

const RENDER_TYPE = [
    ...CHOICE_MULTIPLE_RENDER_TYPE,
    FIELD_RENDER_TYPES.MULTI_SELECT,
    FIELD_RENDER_TYPES.MULTI_SELECT_WITH_LABELS_BELOW,
    FIELD_RENDER_TYPES.MULTI_SELECT_CHECKBOXES,
] as const;

const itemsC = t.intersection([
    t.type({
        enum: t.array(t.string),
        enum_titles: t.array(t.string),
    }),
    t.partial({
        enum_additional_info: t.array(t.number),
        // Backend emits `null` for choices that have no color (e.g. the
        // marker template fields on a Comment report — every choice gets a
        // slot in enum_colors and unset ones come through as null). The
        // null entries are filtered out below so we never set
        // MultiSelectChoice.color to null downstream.
        enum_colors: t.array(t.union([t.string, t.null])),
        enum_tooltips: t.array(t.string),
        enum_disabled: t.array(t.string),
        maxItems: t.Int,
        minItems: t.Int,
    }),
]);

export const RawMultiSelectFieldC = t.intersection([
    RawEvaluationFieldC,
    t.type({
        title: t.union([t.string, t.null]),
        name: t.string,
        items: itemsC,
        default: t.array(t.string),
        type: t.literal('array'),
    }),
    t.partial({
        attr: t.partial({
            placeholder: t.string,
            'data-render-with-labels-below': t.literal(true),
            'data-render-as-tree': t.literal(true),
            'data-empty-title': t.string,
            'data-empty-subtitle': t.string,
            'view-type': t.literal('checkbox'),
            'data-tags': t.literal('true'),
            // Tag validation is the field's own rule, not the component's: two
            // taggable fields on the same form may bound their tags differently.
            'data-tag-max-length': t.string,
            'data-tag-length-message': t.string,
        }),
        autoDeselectAncestors: t.boolean,
        autoSelectDescendants: t.boolean,
    }),
]);

export const MultiSelectChoiceC = t.intersection([
    t.type({
        value: ChoiceValueC,
        title: t.string,
    }),
    t.partial({
        selected: t.boolean,
        disabled: t.boolean,
        evaluationPoints: t.union([t.number, t.null]),
        isNullChoice: t.boolean,
        color: t.string,
        tooltip: t.string,
    }),
]);

export const MultiSelectFieldPartialWithoutRenderAsC = t.intersection([
    t.type({
        choices: t.array(MultiSelectChoiceC),
        selectedChoices: t.array(MultiSelectChoiceC),
        autoDeselectAncestors: t.boolean,
        autoSelectDescendants: t.boolean,
    }),
    t.partial({
        placeholder: t.string,
        emptyTitle: t.string,
        emptySubtitle: t.string,
        taggable: t.boolean,
        tagMaxLength: t.number,
        tagLengthMessage: t.string,
    }),
]);

export const MultiSelectFieldPartialC = t.intersection([
    MultiSelectFieldPartialWithoutRenderAsC,
    t.type({
        renderAs: OneOf(RENDER_TYPE),
    }),
]);

export const MultiSelectFieldC = t.intersection([MultiSelectFieldPartialC, FieldCommonPropsC]);

export type RawMultiSelectField = TypeOf<typeof RawMultiSelectFieldC>
export type MultiSelectChoice = TypeOf<typeof MultiSelectChoiceC>
export type MultiSelectFieldPartial = TypeOf<typeof MultiSelectFieldPartialC>

/**
 * @deprecated MultipleChoice
 */
export type MultiSelectField = TypeOf<typeof MultiSelectFieldC>

/**
 * @deprecated toMultipleChoice instead
 */
function convert(field: RawMultiSelectField): MultiSelectFieldPartial {
    let renderAs = FIELD_RENDER_TYPES.MULTI_SELECT;

    if (isType(field, ChoiceMultipleExpandedFieldC)) {
        renderAs = getChoiceMultipleExpandedRenderType(field as ChoiceMultipleExpandedField);
    }

    const withEnumAfterInput = field.attr?.['data-render-with-labels-below'] || false;
    if (withEnumAfterInput) {
        renderAs = FIELD_RENDER_TYPES.MULTI_SELECT_WITH_LABELS_BELOW;
    } else if (field.attr?.['view-type'] === 'checkbox') {
        renderAs = FIELD_RENDER_TYPES.MULTI_SELECT_CHECKBOXES;
    }

    const placeholder = field.attr?.placeholder;
    const emptyTitle = field.attr?.['data-empty-title'];
    const emptySubtitle = field.attr?.['data-empty-subtitle'];
    const taggable = field.attr?.['data-tags'] === 'true';
    const rawTagMaxLength = Number(field.attr?.['data-tag-max-length']);
    const tagMaxLength = Number.isFinite(rawTagMaxLength) && rawTagMaxLength > 0
        ? rawTagMaxLength
        : undefined;
    const tagLengthMessage = field.attr?.['data-tag-length-message'];
    const choices = field.items.enum.map((val, i) => {
        // enum_colors may contain `null` for choices without a color. Pass
        // through only string values so the resulting MultiSelectChoice.color
        // stays compatible with `t.string` in MultiSelectChoiceC.
        const rawColor = field.items.enum_colors?.[i];
        const color = typeof rawColor === 'string' ? rawColor : undefined;

        return {
            value: val,
            title: field.items.enum_titles[i],
            selected: field.default ? field.default.includes(val) : false,
            evaluationPoints: field.items.enum_additional_info?.[i] ?? null,
            disabled: field.items.enum_disabled?.includes(val) || false,
            color,
            tooltip: field.items.enum_tooltips?.[i],
        };
    });

    // Taggable fields (e.g. keyword terms) may carry operator-added values that
    // are not part of the predefined enum. Synthesize choices for them so they
    // render as selected chips instead of tripping the assertion below.
    if (taggable) {
        field.default.forEach((value) => {
            if (!choices.some((choice) => choice.value === value)) {
                choices.push({
                    value,
                    title: value,
                    selected: true,
                    evaluationPoints: null,
                    disabled: false,
                    color: undefined,
                    tooltip: undefined,
                });
            }
        });
    }

    const selectedChoices = field.default
        .map((defaultValue) => choices.find((choice) => choice.value === defaultValue));
    assertAllElementsAreDefined(selectedChoices);

    const autoDeselectAncestors = field.autoDeselectAncestors || false;
    const autoSelectDescendants = field.autoSelectDescendants || false;

    return {
        renderAs,
        choices,
        selectedChoices,
        placeholder,
        emptyTitle,
        emptySubtitle,
        taggable,
        tagMaxLength,
        tagLengthMessage,
        autoDeselectAncestors,
        autoSelectDescendants,
        ...getEvaluationProps(field),
    };
}

export default convert;
